import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PortableText, type PortableTextBlock } from '@portabletext/react';
import { Loader2 } from 'lucide-react';
import { AuthorCard } from '../../components/blog/AuthorCard';
import { BlogContent } from '../../components/blog/BlogContent';
import { RelatedPosts } from '../../components/blog/RelatedPosts';
import { portableTextComponents } from '../../components/blog/portableTextComponents';
import { PageSeo } from '../../components/seo/PageSeo';
import { usePrerenderData } from '../../context/PrerenderDataContext';
import { MarketingLandingShell } from '../../components/marketing/MarketingLandingShell';
import { blogPostTitle, buildBlogPostingJsonLd, formatPostAuthorNames } from '../../lib/blogSchema';
import {
  getPostBySlug,
  getRelatedPosts,
  resolvePostSeoImage,
  safeImageSrcSet,
  safeImageUrl,
} from '../../lib/sanity';
import { useSanityFetch } from '../../lib/useSanityFetch';
import { CATEGORY_LABELS } from './categoryLabels';
import { renderedImageDimensions, imageAspectRatio } from '../../lib/imageDimensions';

export default function PostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const prerenderData = usePrerenderData();
  const prerenderPost = prerenderData?.post;
  const prerenderRelated = prerenderData?.relatedPosts;
  const hasPrerenderPost = prerenderPost !== undefined;

  const { data: fetchedPost, loading, error } = useSanityFetch(
    () => getPostBySlug(slug!),
    [slug],
    { enabled: !hasPrerenderPost },
  );

  const post = hasPrerenderPost ? prerenderPost : fetchedPost;
  const isLoading = !hasPrerenderPost && loading;

  const { data: fetchedRelated } = useSanityFetch(
    () => getRelatedPosts(post!),
    [post?._id],
    { enabled: !!post && prerenderRelated === undefined },
  );

  const relatedPosts = prerenderRelated ?? fetchedRelated ?? [];

  const seoImage = post
    ? resolvePostSeoImage(post) ??
      safeImageUrl(post.coverImage, (b) => b.width(1200).height(630).format('jpg').quality(80)) ??
      undefined
    : undefined;

  useEffect(() => {
    if (hasPrerenderPost) return;
    if (!loading && !error && fetchedPost === null) {
      navigate('/blog', { replace: true });
    }
  }, [hasPrerenderPost, loading, error, fetchedPost, navigate]);

  if (!hasPrerenderPost && !loading && !error && fetchedPost === null) {
    return null;
  }

  if (isLoading) {
    return (
      <MarketingLandingShell>
        <BlogContent>
          <div className="flex justify-center py-20">
            <Loader2 size={32} className="animate-spin text-primary" aria-label="Loading post" />
          </div>
        </BlogContent>
      </MarketingLandingShell>
    );
  }

  if (error || !post) {
    return (
      <MarketingLandingShell>
        <BlogContent>
          <div className="py-20 text-center">
            <p className="text-on-surface-variant">Failed to load post.</p>
            <Link
              to="/blog"
              className="mt-4 inline-block font-mono text-[10px] tracking-[0.2em] uppercase text-primary hover:text-[#ffcfbf]"
            >
              ← Back to blog
            </Link>
          </div>
        </BlogContent>
      </MarketingLandingShell>
    );
  }

  const coverUrl = safeImageUrl(post.coverImage, (b) => b.width(1200).auto('format'));
  const coverSrcSet = safeImageSrcSet(post.coverImage, 1200, (b, width) => b.width(width).auto('format'));
  const coverDimensions = renderedImageDimensions(post.coverImage, 1200);
  const coverAspectRatio = imageAspectRatio(post.coverImage);

  const date = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  const authorNames = formatPostAuthorNames(post);

  return (
    <>
      <PageSeo
        meta={{
          title: blogPostTitle(post.seo?.metaTitle ?? post.title),
          description: post.seo?.metaDescription ?? post.excerpt,
          path: `/blog/${post.slug.current}`,
        }}
        ogType="article"
        ogImage={seoImage}
        article={
          authorNames ? { publishedTime: post.publishedAt, author: authorNames } : undefined
        }
        jsonLd={buildBlogPostingJsonLd(post, seoImage)}
      />

      <MarketingLandingShell>
        <BlogContent>
          <article>
            <header className="mb-8">
              <Link
                to="/blog"
                className="inline-block font-mono text-[10px] tracking-[0.2em] uppercase text-on-surface-muted transition-colors hover:text-primary"
              >
                ← Back to blog
              </Link>

              <div className="mt-6 flex items-center gap-3">
                {post.category && (
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-primary">
                    {CATEGORY_LABELS[post.category] ?? post.category}
                  </span>
                )}
                {date && <span className="text-[13px] text-on-surface-muted">{date}</span>}
              </div>

              <h1 className="font-display m-0 mt-4 text-[clamp(28px,3.5vw,44px)] leading-[1.05] tracking-[-0.02em] text-[#f2efea]">
                {post.title}
              </h1>
              {post.excerpt && (
                <p className="mt-4 border-l-2 border-white/10 pl-4 text-[17px] leading-[1.65] text-on-surface-variant sm:text-[18px]">
                  {post.excerpt}
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-6">
                <AuthorCard author={post.author} />
                {post.coAuthors?.map((coAuthor, index) =>
                  coAuthor ? (
                    <AuthorCard key={coAuthor.slug?.current ?? index} author={coAuthor} />
                  ) : null,
                )}
              </div>
            </header>

            {coverUrl && (
              <div className="mb-10" style={coverAspectRatio ? { aspectRatio: coverAspectRatio } : undefined}>
                <img
                  src={coverUrl}
                  srcSet={coverSrcSet ?? undefined}
                  alt={post.coverImage?.alt ?? post.title}
                  width={coverDimensions?.width}
                  height={coverDimensions?.height}
                  sizes="(max-width: 1200px) 100vw, 1200px"
                  className="h-auto w-full"
                />
              </div>
            )}

            <div className="blog-prose text-on-surface-variant">
              <PortableText
                value={post.body as PortableTextBlock[]}
                components={portableTextComponents}
              />
            </div>

            <RelatedPosts posts={relatedPosts} />
          </article>
        </BlogContent>
      </MarketingLandingShell>
    </>
  );
}
